/* ==========================================================================
   KLYRO — storefront behaviour  (v4, vanilla, no deps)
   --------------------------------------------------------------------------
   Motion posture: Emil Kowalski + apple-design.
   - One authored focal moment (hero entrance).
   - One signature moment (scroll story: sticky media + progress choreography).
   - Supporting motion is feedback / state / spatial only:
       purchase-module state crossfade (blur-masked), cart drawer + upsell
       sheet with drag-to-dismiss + velocity, sticky bar mirroring the CTA.
   - transform / opacity / filter / clip-path only. Reduced-motion honoured.
   ========================================================================== */
(function () {
  "use strict";

  var RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  /* Mark JS present immediately (this file is deferred, so .klyro nodes exist).
     Only now may CSS hide entrance elements — a failed/blocked script leaves
     the page fully visible. */
  try { $$(".klyro").forEach(function (el) { el.classList.add("k-js"); }); } catch (e) {}
  var on = function (el, ev, fn, o) { if (el) el.addEventListener(ev, fn, o || false); };
  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };
  var lock = function (v) { document.body.classList.toggle("k-lock", !!v); };

  /* boot() re-runs on every shopify:section:load (constant in the Theme Editor).
     once(el,key) returns true only the first time for a given node, so handlers
     never stack. A genuinely re-rendered section gets fresh nodes → re-inits. */
  function once(el, key) {
    if (!el) return false;
    if (el.getAttribute("data-k-once-" + key)) return false;
    el.setAttribute("data-k-once-" + key, "1");
    return true;
  }

  /* Handles both "${{amount}}" (US) and "€{{amount_with_comma_separator}}" (EU). */
  function grp(intStr, sep) { return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
  /* money(cents, currencyCode?)
     Formats a raw cent amount in the visitor's MARKET currency. No symbol is
     ever hardcoded — the currency code comes from Shopify (cart.currency.iso_code,
     or the code passed in from a /cart.js response) and Intl renders the correct
     symbol + separators for the active locale. Used only for values computed in
     JS (cart totals, multipacks); fixed prices are pre-formatted by Liquid |money.
     Falls back to shop.money_format only if Intl / the currency code is missing. */
  function money(cents, currencyCode) {
    if (cents == null || isNaN(cents)) return "";
    var K = window.KLYRO || {};
    var code = currencyCode || K.currency;
    if (code && typeof Intl !== "undefined" && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(K.locale || undefined, {
          style: "currency", currency: code
        }).format(cents / 100);
      } catch (e) { /* fall through */ }
    }
    var fmt = K.moneyFormat || "{{amount}}";
    var comma = fmt.indexOf("comma_separator") > -1;
    var abs = Math.abs(cents) / 100;
    var p = abs.toFixed(2).split(".");
    var withDec = comma ? grp(p[0], ".") + "," + p[1] : grp(p[0], ",") + "." + p[1];
    var noDec = comma ? grp(String(Math.round(abs)), ".") : grp(String(Math.round(abs)), ",");
    return fmt
      .replace(/\{\{\s*amount_no_decimals(_with_comma_separator)?\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/g, withDec);
  }

  /* route helpers — respect Shopify.routes.root / KLYRO.routes so the store
     works under a locale or market sub-path, never a hardcoded "/". */
  function cartRoute(kind) {
    var K = (window.KLYRO && window.KLYRO.routes) || {};
    var S = (window.Shopify && window.Shopify.routes) || {};
    var root = (S.root || K.root || "/").replace(/\/+$/, "") + "/";
    var map = { get: "cart.js", add: "cart/add.js", update: "cart/update.js", change: "cart/change.js", cart: "cart" };
    return root + map[kind];
  }
  var Cart = {
    async get() { var r = await fetch(cartRoute("get"), { headers: { Accept: "application/json" } }); return r.json(); },
    async add(items) {
      var r = await fetch(cartRoute("add"), { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: items }) });
      var data = await r.json();
      if (!r.ok) { var e = new Error((data && (data.description || data.message)) || "add failed"); e.data = data; throw e; }
      return data;
    },
    async change(key, qty) {
      var r = await fetch(cartRoute("change"), { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: key, quantity: qty }) });
      return r.json();
    },
    /* atomic multi-line update — keeps bundle components synchronised */
    async update(updates) {
      var r = await fetch(cartRoute("update"), { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ updates: updates }) });
      return r.json();
    }
  };
  function newBundleId() {
    return "kb-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 7);
  }

  /* ---------------------------------------------------------------- Toast */
  var toastEl;
  function toast(msg) {
    if (!toastEl) { toastEl = document.createElement("div"); toastEl.className = "k-toast"; document.body.appendChild(toastEl); }
    toastEl.textContent = msg;
    toastEl.setAttribute("data-show", "true");
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.removeAttribute("data-show"); }, 2600);
  }

  /* ============================================================ HEADER */
  function initHeader() {
    var header = $(".k-header");
    if (header && once(header, "header")) {
      var set = function () { header.setAttribute("data-stuck", window.scrollY > 6 ? "true" : "false"); };
      set(); on(window, "scroll", set, { passive: true });
    }
    var nav = $(".k-mobilenav");
    if (nav && !once(nav, "nav")) return;
    var setNav = function (v) { if (nav) { nav.setAttribute("data-open", v ? "true" : "false"); lock(v); } };
    on($("[data-k-nav-open]"), "click", function () { setNav(true); });
    on($("[data-k-nav-close]"), "click", function () { setNav(false); });
    $$(".k-mobilenav__list a").forEach(function (a) { on(a, "click", function () { setNav(false); }); });
  }

  /* ================================ REVEAL — headings (.k-fade) + groups (.k-in)
     .k-io is a no-op hook: it never hides its element, it only gains .is-in when
     scrolled into view (used to trigger a decorative wipe without risking blank
     text if the observer never fires). */
  function initReveal() {
    var els = $$(".k-fade, .k-fade--left, .k-fade--right, .k-fade--sink, .k-reveal-clip, .k-in, .k-io")
      .filter(function (e) { return once(e, "reveal"); });
    if (!els.length) return;
    var showAll = function () { els.forEach(function (e) { e.classList.add("is-in"); }); };
    if (RM || !("IntersectionObserver" in window)) { showAll(); return; }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
    els.forEach(function (e) { io.observe(e); });
    /* FAILSAFE: whatever happens with the observer (editor iframe, off-screen
       first paint, a throw elsewhere), nothing stays invisible past ~1.1s. */
    setTimeout(showAll, 1100);
  }

  /* ================================ HERO GALLERY — click-to-swap (PDP style) */
  function initHeroGallery() {
    $$(".k-hero__figure").forEach(setupHeroFigure);
  }
  function setupHeroFigure(fig) {
    if (!fig || !once(fig, "herogal")) return;
    var gal = fig.closest("[data-k-gallery]") || document;
    var main = $("[data-k-hero-main]", fig);
    var thumbs = $$(".k-hero__thumb", gal);
    var thumbStrip = $(".k-hero__thumbs", gal);
    if (!main) return;

    /* low-level: crossfade the main image to an arbitrary src */
    function swapTo(src, cover) {
      if (!src) return;
      if (src === main.getAttribute("src")) { main.classList.toggle("is-cover", !!cover); return; }
      fig.classList.add("is-swapping");
      var pre = new Image(); pre.src = src;
      function done() {
        main.setAttribute("src", src);
        main.classList.toggle("is-cover", !!cover);
        requestAnimationFrame(function () { fig.classList.remove("is-swapping"); });
      }
      if (pre.decode) pre.decode().then(done, done); else { pre.onload = done; pre.onerror = done; }
    }
    var idx = Math.max(0, thumbs.findIndex(function (t) { return t.getAttribute("aria-current") === "true"; }));

    /* scroll ONLY the thumb strip, horizontally — never let the page jump
       (a vertical scrollIntoView between two arrow clicks was moving the arrow
       out from under the cursor, so the 2nd click "did nothing") */
    function revealThumb(btn) {
      if (!thumbStrip || !btn) return;
      var s = thumbStrip.getBoundingClientRect(), b = btn.getBoundingClientRect();
      if (b.left < s.left) thumbStrip.scrollLeft -= (s.left - b.left) + 24;
      else if (b.right > s.right) thumbStrip.scrollLeft += (b.right - s.right) + 24;
    }
    function activate(btn) {
      if (!btn) return;
      var n = thumbs.indexOf(btn);
      if (n > -1) idx = n;
      swapTo(btn.getAttribute("data-src"), btn.getAttribute("data-cover") === "1");
      thumbs.forEach(function (t) { t.setAttribute("aria-current", t === btn ? "true" : "false"); });
      revealThumb(btn);
    }
    function step(dir) {
      if (thumbs.length < 2) return;
      idx = (idx + dir + thumbs.length) % thumbs.length;
      activate(thumbs[idx]);
    }
    thumbs.forEach(function (t) { on(t, "click", function () { activate(t); }); });

    /* prev / next arrows — direct listeners on every matching button (forEach so
       a missing node is a no-op, never a null crash), PLUS a delegated fallback
       on the figure. Hidden entirely when there's nothing to page through. */
    var navs = $$(".k-hero__nav", fig);
    if (thumbs.length < 2) {
      navs.forEach(function (n) { n.hidden = true; });
    } else {
      $$(".k-hero__nav--prev", fig).forEach(function (b) { on(b, "click", function (e) { e.preventDefault(); step(-1); }); });
      $$(".k-hero__nav--next", fig).forEach(function (b) { on(b, "click", function (e) { e.preventDefault(); step(1); }); });
    }
    on(fig, "keydown", function (e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    });
    /* swipe on touch */
    var sx = 0, sy = 0, sw = false;
    on(fig, "touchstart", function (e) { var t = e.changedTouches[0]; sx = t.clientX; sy = t.clientY; sw = true; }, { passive: true });
    on(fig, "touchend", function (e) {
      if (!sw) return; sw = false;
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.6) step(dx < 0 ? 1 : -1);
    }, { passive: true });

    /* variant → matching thumbnail, called from the buy module */
    fig._go = function (mediaId) {
      var m = thumbs.find(function (t) { return t.getAttribute("data-media-id") === String(mediaId); });
      if (m) activate(m);
    };
    /* direct src swap (per-colour override image), also from the buy module */
    fig._src = function (url, cover) {
      swapTo(url, cover !== false);
      thumbs.forEach(function (t) { t.setAttribute("aria-current", "false"); });
    };
    if (gal && gal !== document) { gal._go = fig._go; gal._src = fig._src; }
  }

  /* ================================ PRIMARY CTA — one-shot highlight on first view */
  function initCtaLight() {
    var atc = $("[data-k-atc]");
    if (!atc || !once(atc, "ctalight") || RM || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.disconnect();
        setTimeout(function () {
          atc.classList.add("is-lit");
          atc.addEventListener("animationend", function h() { atc.classList.remove("is-lit"); atc.removeEventListener("animationend", h); });
        }, 240);
      });
    }, { threshold: 0.9 });
    io.observe(atc);
  }

  /* =============================================================== FAQ */
  function initFaq() {
    $$(".k-faq__item").forEach(function (item) {
      if (!once(item, "faq")) return;
      var q = $(".k-faq__q", item);
      on(q, "click", function () {
        var open = item.getAttribute("data-open") === "true";
        item.setAttribute("data-open", open ? "false" : "true");
        q.setAttribute("aria-expanded", open ? "false" : "true");
      });
    });
  }

  /* ============================================ STEP CAROUSEL (Connect/Fill/…)
     Auto-advances, click a step to jump, pauses on hover, starts in view.
     (Replaced the scroll-driven version — it was fragile and hard to read.) */
  function initStory() {
    $$("[data-k-story]").forEach(function (root) {
      if (!once(root, "story")) return;
      var steps = $$(".k-story__step", root);
      var frames = $$(".k-story__frame", root);
      var bar = $(".k-story__rail-fill", root);
      if (!steps.length) return;
      var DWELL = parseInt(root.getAttribute("data-dwell"), 10) || 4200;
      var i = 0, timer = null, hovering = false, raf = 0;

      function show(n) {
        i = (n + steps.length) % steps.length;
        steps.forEach(function (s, si) {
          s.setAttribute("data-active", si === i ? "true" : "false");
          var b = s.querySelector("button"); if (b) b.setAttribute("aria-pressed", si === i ? "true" : "false");
        });
        frames.forEach(function (f, fi) {
          f.setAttribute("data-active", fi === i ? "true" : "false");
          f.setAttribute("aria-hidden", fi === i ? "false" : "true");
        });
      }
      function tickBar() {
        if (RM || !bar) return;
        var t0 = performance.now();
        cancelAnimationFrame(raf);
        (function step(now) {
          if (hovering) { raf = requestAnimationFrame(step); return; }
          var p = clamp((now - t0) / DWELL, 0, 1);
          bar.style.transform = "scaleX(" + p + ")";
          if (p < 1) raf = requestAnimationFrame(step);
        })(t0);
      }
      function restart() {
        clearInterval(timer);
        if (RM) return;
        tickBar();
        timer = setInterval(function () { if (!hovering) { show(i + 1); tickBar(); } }, DWELL);
      }

      steps.forEach(function (s, si) {
        var btn = s.querySelector("button") || s;
        on(btn, "click", function () { show(si); restart(); });
        on(s, "mouseenter", function () { hovering = true; });
        on(s, "mouseleave", function () { hovering = false; });
      });
      on(root, "mouseleave", function () { hovering = false; });

      show(0);
      if (RM || !("IntersectionObserver" in window)) { restart(); return; }
      new IntersectionObserver(function (ents) {
        ents.forEach(function (e) { e.isIntersecting ? restart() : (clearInterval(timer), cancelAnimationFrame(raf)); });
      }, { threshold: 0.3 }).observe(root);
    });
  }

  /* ========================================================= GALLERY */
  function initGallery() {
    $$("[data-k-gallery]").forEach(function (root) {
      var track = $(".k-gallery__track", root);
      var slides = $$(".k-gallery__slide", root);
      var thumbs = $$(".k-gallery__thumb", root);
      var dots = $$(".k-gallery__dots button", root);
      var prev = $(".k-gallery__nav--prev", root);
      var next = $(".k-gallery__nav--next", root);
      if (!track || !slides.length) return;
      var idx = 0;
      var go = function (i, smooth) {
        idx = clamp(i, 0, slides.length - 1);
        track.scrollTo({ left: slides[idx].offsetLeft, behavior: smooth === false || RM ? "auto" : "smooth" });
        sync();
      };
      var sync = function () {
        thumbs.forEach(function (t, i) { t.setAttribute("aria-current", i === idx ? "true" : "false"); });
        dots.forEach(function (d, i) { d.setAttribute("aria-current", i === idx ? "true" : "false"); });
      };
      thumbs.forEach(function (t, i) { on(t, "click", function () { go(i); }); });
      dots.forEach(function (d, i) { on(d, "click", function () { go(i); }); });
      on(prev, "click", function () { go(idx - 1); });
      on(next, "click", function () { go(idx + 1); });
      var st;
      on(track, "scroll", function () {
        clearTimeout(st);
        st = setTimeout(function () {
          var n = 0, m = Infinity;
          slides.forEach(function (s, i) { var d = Math.abs(s.offsetLeft - track.scrollLeft); if (d < m) { m = d; n = i; } });
          idx = n; sync();
        }, 90);
      }, { passive: true });
      root._go = function (mediaId) {
        var i = slides.findIndex(function (s) { return s.getAttribute("data-media-id") === String(mediaId); });
        if (i > -1) go(i);
      };
      sync();
    });
  }

  /* ================================================== OVERLAY + LAYERS */
  var overlay;
  function ensureOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement("div"); overlay.className = "k-overlay";
    document.body.appendChild(overlay);
    on(overlay, "click", closeAll);
    return overlay;
  }
  var lastFocused = null, trapLayer = null;
  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';
  function openLayer(el) {
    lastFocused = document.activeElement;
    ensureOverlay().setAttribute("data-open", "true");
    el.setAttribute("data-open", "true");
    lock(true);
    trapLayer = el;
    /* move focus in, once the transition has begun */
    setTimeout(function () {
      var f = el.querySelector("[data-k-cart-close],[data-k-upsell-decline]") || el.querySelector(FOCUSABLE);
      if (f && f.focus) f.focus();
    }, 60);
  }
  function closeAll() {
    if (overlay) overlay.removeAttribute("data-open");
    $$(".k-drawer, .k-sheet").forEach(function (e) {
      e.removeAttribute("data-open");
      e.removeAttribute("data-dragging");
      e.style.removeProperty("--k-drawer-drag");
      e.style.removeProperty("--k-sheet-drag");
    });
    lock(false);
    trapLayer = null;
    if (lastFocused && lastFocused.focus) { try { lastFocused.focus(); } catch (e) {} }
    lastFocused = null;
  }
  /* tap/click anywhere outside an open layer closes it — a reliable backstop to
     the overlay's own click handler (hit-testing under the hoisted wrapper can be
     flaky). Only fires while a layer is open. */
  on(document, "pointerdown", function (e) {
    if (!trapLayer) return;
    if (e.target.closest(".k-drawer, .k-sheet, [data-k-cart-open], [data-k-upsell]")) return;
    closeAll();
  }, true);
  on(document, "keydown", function (e) {
    if (e.key === "Escape") { closeAll(); return; }
    /* focus trap while a layer is open */
    if (e.key === "Tab" && trapLayer) {
      var items = $$(FOCUSABLE, trapLayer).filter(function (n) { return n.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!trapLayer.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
  });

  /* Drag-to-dismiss — pointer capture + velocity projection (apple §5–6) */
  function makeDismissable(el, axis, onClose) {
    if (!el) return;
    var prop = axis === "x" ? "--k-drawer-drag" : "--k-sheet-drag";
    var startPos = 0, cur = 0, hist = [], grabbing = false;
    var handle = axis === "x" ? el.querySelector(".k-drawer__head") : el.querySelector(".k-sheet__grab") || el;

    function down(e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      /* NEVER start a drag from an interactive control — the click / form submit
         must pass through untouched (this was eating the ✕ and Checkout taps). */
      if (e.target.closest("button, a, input, select, textarea, label, [role='button']")) return;
      if (axis === "x") {
        /* drawer: drag only from the header bar, never the body or the footer */
        if (!handle || !handle.contains(e.target)) return;
      } else if (e.target.closest(".k-sheet__row, .k-btn")) {
        return;
      }
      grabbing = true;
      startPos = axis === "x" ? e.clientX : e.clientY;
      cur = 0; hist = [{ p: 0, t: performance.now() }];
      el.setAttribute("data-dragging", "true");
      try { el.setPointerCapture && el.setPointerCapture(e.pointerId); } catch (_) {}
    }
    function move(e) {
      if (!grabbing) return;
      var d = (axis === "x" ? e.clientX : e.clientY) - startPos;
      cur = Math.max(0, d);                 /* only the dismiss direction */
      /* rubber-band a touch when pushed the wrong way */
      if (d < 0) cur = d * 0.18;
      el.style.setProperty(prop, cur + "px");
      hist.push({ p: cur, t: performance.now() });
      if (hist.length > 6) hist.shift();
    }
    function up() {
      if (!grabbing) return;
      grabbing = false;
      el.removeAttribute("data-dragging");
      var a = hist[0], b = hist[hist.length - 1];
      var v = (b.p - a.p) / Math.max(1, b.t - a.t);   /* px/ms */
      var size = axis === "x" ? el.offsetWidth : el.offsetHeight;
      if (cur > size * 0.4 || v > 0.5) { onClose(); }
      else { el.style.setProperty(prop, "0px"); }
    }
    on(el, "pointerdown", down);
    on(el, "pointermove", move);
    on(el, "pointerup", up);
    on(el, "pointercancel", up);
  }

  /* =========================================================== CART DRAWER */
  var CartDrawer = {
    el: null,
    init: function () {
      this.el = $("[data-k-cart-drawer]");
      if (!this.el) return;
      var self = this;
      /* hoist the drawer (with its .klyro wrapper, to keep scoping) to <body> so
         it can't be trapped under the overlay by a stacking context on an
         ancestor (header group etc.) */
      var wrap = this.el.closest(".klyro") || this.el;
      /* drop any stale drawer hoisted by a previous section re-render */
      $$("[data-k-cart-drawer]").forEach(function (d) {
        if (d !== self.el) { var w = d.closest(".klyro") || d; if (w.parentNode) w.parentNode.removeChild(w); }
      });
      if (wrap.parentNode !== document.body) document.body.appendChild(wrap);
      /* [data-k-cart-open] buttons live in the header (not re-rendered with
         .klyro sections), so guard them individually against stacking. */
      $$("[data-k-cart-open]").forEach(function (b) {
        if (!once(b, "cartopen")) return;
        on(b, "click", function (e) { e.preventDefault(); self.open(); });
      });
      if (once(this.el, "cartdrawer")) {
        /* ONE delegated click handler on the drawer shell — survives every AJAX
           re-render of the body, so qty / remove never go dead. */
        on(this.el, "click", function (e) { self.onClick(e); });
        makeDismissable(this.el, "x", closeAll);
      }
      this.refresh();
    },
    open: function () { if (!this.el) { this.el = $("[data-k-cart-drawer]"); } if (this.el) openLayer(this.el); this.refresh(); },
    lastBundleId: null,
    busy: false,
    /* split cart.items into ordered blocks: {type:'bundle',...} | {type:'line', item} */
    groupCart: function (cart) {
      var blocks = [], map = {};
      (cart.items || []).forEach(function (it) {
        var p = it.properties || {};
        var bid = p._bundle_id;
        var cmp = parseInt(p._cmp, 10);
        var lineWas = (cmp && cmp > 0) ? cmp * it.quantity : it.final_line_price;
        if (bid) {
          if (!map[bid]) { map[bid] = { type: "bundle", id: bid, name: p._bundle_name || "Bundle", subtitle: p._bundle_subtitle || "", badge: p._bundle_badge || "", lines: [], total: 0, was: 0, save: 0, qty: 0 }; blocks.push(map[bid]); }
          var g = map[bid];
          g.lines.push(it);
          g.total += it.final_line_price;
          g.was += lineWas;
          if (p._bundle_role === "main") g.qty = it.quantity;
        } else {
          blocks.push({ type: "line", item: it });
        }
      });
      blocks.forEach(function (bk) {
        if (bk.type !== "bundle") return;
        if (!bk.qty) bk.qty = bk.lines.reduce(function (m, l) { return Math.max(m, l.quantity); }, 1);
        bk.save = Math.max(0, Math.round(bk.was - bk.total));
        bk.savePct = (bk.save > 0 && bk.was > 0) ? Math.round((bk.save / bk.was) * 100) : 0;
      });
      return blocks;
    },
    /* if a kit's component quantities drifted apart, snap them back together */
    heal: async function (blocks) {
      var fix = null;
      blocks.forEach(function (bk) {
        if (bk.type !== "bundle") return;
        var target = bk.qty;
        bk.lines.forEach(function (l) { if (l.quantity !== target) { fix = fix || {}; fix[l.key] = target; } });
      });
      if (fix) { try { return await Cart.update(fix); } catch (e) {} }
      return null;
    },
    render: function (cart, skipHeal) {
      var self = this;
      var body = $("[data-k-cart-body]", this.el);
      var foot = $("[data-k-cart-foot]", this.el);
      if (!body) return;
      var cur = cart.currency || (window.KLYRO && window.KLYRO.currency);
      if (!cart.items || !cart.items.length) {
        body.innerHTML = '<div class="k-drawer__empty">Your cart is empty.</div>';
        if (foot) foot.hidden = true;
        return;
      }
      if (foot) foot.hidden = false;
      var blocks = this.groupCart(cart);

      function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
      /* no src → the wrapper (.k-cart-line__img / .k-cartkit__thumb) is itself a
         styled placeholder box, so an empty string is the right fallback here */
      function thumb(src) { return src ? '<img src="' + src.replace(/(\.[a-z]+)(\?|$)/, "_120x$1$2") + '" alt="" loading="lazy">' : ""; }
      function lineOpts(it) {
        return (it.options_with_values || []).filter(function (o) { return o.value && o.value !== "Default Title"; }).map(function (o) { return o.value; }).join(" · ");
      }
      function lineHTML(it) {
        var o = lineOpts(it);
        var p = it.properties || {};
        var cmp = parseInt(p._cmp, 10);
        var wasLine = (cmp && cmp > 0) ? cmp * it.quantity
          : (it.original_line_price > it.final_line_price ? it.original_line_price : 0);
        var priceBlock = (wasLine && wasLine > it.final_line_price)
          ? '<s class="k-cart-line__was">' + money(wasLine, cur) + "</s>" +
            '<div class="k-cart-line__price">' + money(it.final_line_price, cur) + "</div>"
          : '<div class="k-cart-line__price">' + money(it.final_line_price, cur) + "</div>";
        return '<div class="k-cart-line" data-key="' + esc(it.key) + '">' +
          '<div class="k-cart-line__img">' + thumb(it.image) + "</div>" +
          '<div><div class="k-cart-line__name">' + esc(it.product_title) + "</div>" +
          (o ? '<div class="k-cart-line__opt">' + esc(o) + "</div>" : "") +
          '<div class="k-qty"><button type="button" data-k-qty="-1" aria-label="Decrease">−</button><span aria-live="polite">' + it.quantity + '</span><button type="button" data-k-qty="1" aria-label="Increase">+</button></div></div>' +
          '<div class="k-cart-line__col2">' + priceBlock + '<button type="button" class="k-cart-line__remove" data-k-remove>Remove</button></div></div>';
      }
      function kitHTML(g) {
        var isNew = g.id === self.lastBundleId;
        var rows = g.lines.map(function (l) {
          var o = lineOpts(l);
          return '<div class="k-cartkit__row">' +
            '<div class="k-cartkit__thumb">' + thumb(l.image) + "</div>" +
            '<div class="k-cartkit__rowname">' + esc(l.product_title) + (o ? ' <span>— ' + esc(o) + "</span>" : "") + "</div>" +
            "</div>";
        }).join("");
        var keys = g.lines.map(function (l) { return l.key; }).join(",");
        var hasSave = g.save > 0 && g.savePct > 0;
        return '<div class="k-cartkit" data-bundle-id="' + esc(g.id) + '" data-line-keys="' + esc(keys) + '" data-qty="' + g.qty + '"' + (isNew ? ' data-new="true"' : "") + '>' +
          '<div class="k-cartkit__head">' +
            (g.badge ? '<span class="k-cartkit__badge">' + esc(g.badge) + "</span>" : "") +
            '<span class="k-cartkit__name">' + esc(g.name) + "</span>" +
            (g.subtitle ? '<span class="k-cartkit__sub">' + esc(g.subtitle) + "</span>" : "") +
          "</div>" +
          '<div class="k-cartkit__items">' + rows + "</div>" +
          '<div class="k-cartkit__value">' +
            (hasSave ? '<div class="k-cartkit__vrow"><span>Bought separately</span><s>' + money(g.was, cur) + "</s></div>" : "") +
            '<div class="k-cartkit__vrow k-cartkit__vrow--total"><span>Bundle total</span><b>' + money(g.total, cur) + "</b></div>" +
            (hasSave ? '<div class="k-cartkit__save">You save ' + money(g.save, cur) + " · " + g.savePct + "% off</div>" : "") +
          "</div>" +
          '<div class="k-cartkit__foot">' +
            '<div class="k-qty k-qty--kit" data-kit-qty>' +
              '<button type="button" data-kit-step="-1" aria-label="Decrease kit quantity">−</button>' +
              '<span aria-live="polite">' + g.qty + "</span>" +
              '<button type="button" data-kit-step="1" aria-label="Increase kit quantity">+</button>' +
            "</div>" +
          "</div>" +
          '<button type="button" class="k-cartkit__remove" data-kit-remove>Remove kit</button>' +
        "</div>";
      }

      body.innerHTML = blocks.map(function (bk) { return bk.type === "bundle" ? kitHTML(bk) : lineHTML(bk.item); }).join("");
      /* quiet settle after every re-render (qty change, remove) — not on reduced motion */
      if (!RM) { body.setAttribute("data-refreshed", "1"); setTimeout(function () { body.removeAttribute("data-refreshed"); }, 260); }

      /* ---- cart-level totals + savings ---- */
      var cartWas = 0;
      blocks.forEach(function (bk) {
        if (bk.type === "bundle") { cartWas += bk.was; return; }
        var it = bk.item, p = it.properties || {};
        var cmp = parseInt(p._cmp, 10);
        cartWas += (cmp && cmp > 0) ? cmp * it.quantity
          : (it.original_line_price > it.final_line_price ? it.original_line_price : it.final_line_price);
      });
      var cartSave = Math.max(0, Math.round(cartWas - cart.total_price));
      var sub = $("[data-k-cart-subtotal]", this.el);
      if (sub) { self.pulseIfChanged(sub, money(cart.total_price, cur)); }
      var savingsBox = $("[data-k-cart-savings]", this.el);
      if (savingsBox) {
        savingsBox.hidden = !(cartSave > 0);
        var amtEl = $("[data-k-cart-savings-amt]", savingsBox);
        if (amtEl && cartSave > 0) self.pulseIfChanged(amtEl, money(cartSave, cur));
      }
      var coEl = $("[data-k-checkout-label]", this.el);
      if (coEl) coEl.textContent = "Checkout · " + money(cart.total_price, cur);

      this._blocks = blocks;
      this.lastBundleId = null;

      /* self-heal desynced kits after paint (once per cycle) */
      if (!skipHeal && !this.busy) {
        this.busy = true;
        this.heal(blocks).then(function (healed) {
          self.busy = false;
          if (healed && healed.items) { self.render(healed, true); updateCount(healed); }
        });
      }
    },
    /* flash a value node only when its text actually changes (Emil: feedback, not decoration) */
    pulseIfChanged: function (el, text) {
      if (!el) return;
      if (el.textContent !== text) {
        el.textContent = text;
        if (!RM) { el.setAttribute("data-bump", "true"); setTimeout(function () { el.removeAttribute("data-bump"); }, 420); }
      }
    },
    afterMutate: function (cart) {
      if (!cart || cart.status >= 400) { this.refresh(); return; }
      this.render(cart); updateCount(cart);
    },
    _mutate: function (promise) {
      var self = this;
      promise.then(function (c) { self.afterMutate(c); }).catch(function () { self.refresh(); });
    },
    /* single delegated handler — every control is resolved from the DOM at click
       time, so nothing breaks after an AJAX re-render. */
    onClick: function (e) {
      var self = this, t = e.target;
      if (!t || !t.closest) return;

      if (t.closest("[data-k-cart-close]")) { e.preventDefault(); closeAll(); return; }

      /* ---- standalone line ---- */
      var line = t.closest(".k-cart-line");
      if (line) {
        var key = line.getAttribute("data-key");
        var span = $(".k-qty span", line);
        var q = parseInt(span && span.textContent, 10) || 1;
        var qb = t.closest("[data-k-qty]");
        if (qb && key) {
          e.preventDefault();
          var d = parseInt(qb.getAttribute("data-k-qty"), 10) || 0;
          self._mutate(Cart.change(key, Math.max(0, q + d)));
          return;
        }
        if (t.closest("[data-k-remove]") && key) {
          e.preventDefault();
          line.setAttribute("data-removing", "true");
          setTimeout(function () { self._mutate(Cart.change(key, 0)); }, RM ? 0 : 160);
          return;
        }
      }

      /* ---- bundle kit (one control drives every component line) ---- */
      var card = t.closest(".k-cartkit");
      if (card) {
        var keys = (card.getAttribute("data-line-keys") || "").split(",").filter(Boolean);
        var kq = parseInt(card.getAttribute("data-qty"), 10) || 1;
        if (!keys.length) return;
        var step = t.closest("[data-kit-step]");
        if (step) {
          e.preventDefault();
          var next = Math.max(0, kq + (parseInt(step.getAttribute("data-kit-step"), 10) || 0));
          var up = {}; keys.forEach(function (k) { up[k] = next; });
          if (next === 0) {
            card.setAttribute("data-removing", "true");
            setTimeout(function () { self._mutate(Cart.update(up)); }, RM ? 0 : 160);
          } else {
            card.setAttribute("data-busy", "true");
            self._mutate(Cart.update(up));
          }
          return;
        }
        if (t.closest("[data-kit-remove]")) {
          e.preventDefault();
          var u0 = {}; keys.forEach(function (k) { u0[k] = 0; });
          card.setAttribute("data-removing", "true");
          setTimeout(function () { self._mutate(Cart.update(u0)); }, RM ? 0 : 160);
          return;
        }
      }
    },
    refresh: async function () { try { var c = await Cart.get(); this.render(c); updateCount(c); } catch (e) {} }
  };

  /* logical purchase-unit count: a 3-line kit reads as 1 */
  function logicalCount(cart) {
    if (typeof cart === "number") return cart;
    if (!cart || !cart.items) return 0;
    var mains = {}, n = 0, counted = {};
    cart.items.forEach(function (it) { var p = it.properties || {}; if (p._bundle_id && p._bundle_role === "main") mains[p._bundle_id] = it.quantity; });
    cart.items.forEach(function (it) {
      var p = it.properties || {};
      if (p._bundle_id) { if (!counted[p._bundle_id]) { counted[p._bundle_id] = true; n += mains[p._bundle_id] || it.quantity; } }
      else n += it.quantity;
    });
    return n;
  }
  function updateCount(cart) {
    var n = logicalCount(cart);
    $$("[data-k-cart-count]").forEach(function (el) {
      var was = parseInt(el.textContent, 10) || 0;
      el.textContent = n;
      if (n > 0) el.setAttribute("data-has", ""); else el.removeAttribute("data-has");
      if (!RM && n !== was) { el.setAttribute("data-bump", "true"); setTimeout(function () { el.removeAttribute("data-bump"); }, 420); }
    });
  }

  /* =========================================================== UPSELL SHEET */
  var Upsell = {
    el: null, pending: null, shown: false,
    init: function () {
      this.el = $("[data-k-upsell]");
      if (!this.el || !once(this.el, "upsell")) return;
      var self = this;
      on($("[data-k-upsell-accept]", this.el), "click", function () { self.resolve(true); });
      on($("[data-k-upsell-decline]", this.el), "click", function () { self.resolve(false); });
      makeDismissable(this.el, "y", function () { self.resolve(false); });
    },
    offer: function () {
      var self = this;
      if (!this.el || this.shown || !window.KLYRO.upsellVariantId) return Promise.resolve(false);
      return new Promise(function (res) { self.pending = res; self.shown = true; openLayer(self.el); });
    },
    resolve: function (v) {
      this.el.removeAttribute("data-open");
      this.el.style.removeProperty("--k-sheet-drag");
      if (overlay && !$(".k-drawer[data-open]")) overlay.removeAttribute("data-open");
      var p = this.pending; this.pending = null;
      if (p) p(v);
    }
  };

  /* ======================================================= PURCHASE MODULE */
  function initBuy() {
    var form = $("[data-k-buy]");
    if (!form || !once(form, "buy")) return;
    var cfg = window.KLYRO || {};
    var variants = cfg.variants || {};
    var bundles = cfg.bundles || [];

    var state = { size: cfg.defaultSize || Object.keys(variants)[0], color: cfg.defaultColor, bundle: 0 };

    var sizeBtns = $$("[data-k-size]", form);
    var colorBtns = $$("[data-k-color]", form);
    var bundleBtns = $$("[data-k-bundle]", form);
    var pricing = $("[data-k-pricing]", form) || $(".k-buy__pricing", form);
    var priceNow = $("[data-k-price-now]", form);
    var priceWas = $("[data-k-price-was]", form);
    var priceSave = $("[data-k-price-save]", form);
    var atc = $("[data-k-atc]", form);
    var idInput = $("[data-k-variant-id]", form);
    var gallery = $("[data-k-gallery]");
    var stickyName = $("[data-k-sticky-name]");
    var stickyPrice = $("[data-k-sticky-price]");
    var colorName = $("[data-k-color-name]", form);
    var stockEl = $("[data-k-stock]", form);
    var upsellPriceEl = $("[data-k-upsell-price]");
    var upsellCompareEl = $("[data-k-upsell-compare]");
    if (upsellPriceEl) {
      var upM = cfg.upsellPriceMoney || cfg.upsellPriceText || "";
      upsellPriceEl.textContent = upM ? (/^[+\-]/.test(upM) ? upM : "+ " + upM) : "";
    }
    if (upsellCompareEl) {
      var upC = cfg.upsellCompareMoney || "";
      upsellCompareEl.textContent = upC; upsellCompareEl.hidden = !upC;
    }

    /* raw cents straight from the Shopify variant object (Markets-adjusted at
       render). Used for maths (%, multipacks); display uses the pre-formatted
       *Money strings so the merchant's market money format is respected. */
    function unitPrice(v) { return v ? v.price : 0; }
    function unitCompare(v) { return (v && v.compare && v.compare > v.price) ? v.compare : 0; }
    function firstAvail(map) { if (!map) return null; var k = Object.keys(map).find(function (c) { return map[c].available; }) || Object.keys(map)[0]; return map[k] || null; }
    function currentColors() { return (variants[state.size] && Object.keys(variants[state.size])) || []; }
    /* NEVER reassign state.color here — the shopper's pick is authoritative.
       We still return a *buyable* variant so ATC works even if that exact colour
       has no live variant yet (single-SKU dropship, un-split options): fall back
       to the default colour, then the first available. The swatch never moves. */
    function resolveVariant() {
      var bySize = variants[state.size] || {};
      return bySize[state.color] || bySize[cfg.defaultColor] || firstAvail(bySize) || null;
    }
    /* swap the hero image for the chosen colour. Priority:
       1) per-colour override image (data-img on the swatch, set in the editor)
       2) the variant's own featured image URL (from the config)
       3) the matching gallery thumbnail by media id
       Never leaves a wrong-colour photo showing. */
    function swapColorImage(btn) {
      var vv = (variants[state.size] || {})[state.color] || {};
      var url = (btn && btn.getAttribute("data-img")) || vv.img;
      if (url && gallery && gallery._src) { gallery._src(url, true); return; }
      if (gallery && gallery._go && vv.mediaId) gallery._go(vv.mediaId);
    }
    var accessories = cfg.accessories || {};

    /* accessory variants a tier adds alongside the selected Power Gun */
    function bundleAccessories(bd) {
      return ((bd && bd.includes) || []).map(function (k) { return accessories[k]; }).filter(function (a) { return a && a.id; });
    }
    /* real multi-line payload for /cart/add.js. Every component is its own
       Shopify line item (AutoDS needs the real SKUs). Same bundle shares a
       hidden _bundle_id so the cart UI can group them. */
    /* unit compare-at cents as a string, only when it's a genuine markdown —
       stored as a hidden `_cmp` line-item property so the cart drawer can show
       "You save …" without a second Shopify round-trip. `_`-prefixed → Shopify
       hides it from the customer and from checkout. */
    function cmpProp(o) {
      return (o && o.compare && o.price != null && o.compare > o.price) ? String(o.compare) : null;
    }
    function bundleItems(bd, v, bundleId, qty) {
      qty = qty || 1;
      var accs = bundleAccessories(bd);
      var grouped = accs.length > 0;
      var base = grouped ? {
        _bundle_id: bundleId,
        _bundle_name: bd.name,
        _bundle_subtitle: bd.subtitle || "",
        _bundle_badge: bd.badge || ""
      } : {};
      var mainProps = assign({}, base, grouped ? { _bundle_role: "main" } : {});
      var mc = cmpProp(v);
      if (mc) mainProps._cmp = mc;
      var out = [{ id: v.id, quantity: qty, properties: Object.keys(mainProps).length ? mainProps : undefined }];
      accs.forEach(function (a) {
        var p = assign({}, base, { _bundle_role: "accessory" });
        var ac = cmpProp(a);
        if (ac) p._cmp = ac;
        out.push({ id: a.id, quantity: qty, properties: p });
      });
      return out;
    }
    function assign(t) { for (var i = 1; i < arguments.length; i++) { var s = arguments[i]; if (s) for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k]; } return t; }

    /* canonical fingerprint of a bundle = its {variantId:role} set (order-independent),
       so "same tier + same Power Gun colour" collapses onto one cart card. A different
       gun colour, or a different tier (extra towel), yields a different signature. */
    function bundleSig(items) {
      return items.map(function (i) {
        return i.id + ":" + ((i.properties && i.properties._bundle_role) || "");
      }).sort().join("|");
    }
    function findBundleBySig(cart, sig) {
      var groups = {}, hit = null;
      ((cart && cart.items) || []).forEach(function (it) {
        var bid = (it.properties || {})._bundle_id;
        if (bid) (groups[bid] = groups[bid] || []).push(it);
      });
      Object.keys(groups).forEach(function (bid) {
        var s = groups[bid].map(function (l) {
          return l.variant_id + ":" + ((l.properties || {})._bundle_role || "");
        }).sort().join("|");
        if (s === sig) hit = { id: bid, lines: groups[bid] };
      });
      return hit;
    }

    /* per-unit "regular value": the variant's compare-at when it's a real
       markdown, else its plain price (so an item with no compare-at contributes
       nothing to the saving). */
    function regUnit(o) {
      if (!o) return 0;
      return (o.compare && o.compare > o.price) ? o.compare : (o.price || 0);
    }
    /* Returns { nowCents, wasCents, saveCents, savePct, hasSave, nowM, wasM, saveM, qty }.
       nowCents  = SUM of the real Shopify component prices (base variant + each accessory)
       wasCents  = SUM of the real Shopify compare-at prices (regular combined value)
       saveCents = wasCents - nowCents ; savePct = round(saveCents / wasCents)
       Every figure is derived from Shopify price objects (Markets-adjusted at
       render). No saving is shown unless a real compare-at discount exists. */
    function bundleTotals(bd, v) {
      bd = bd || {};
      var qty = bd.qty || 1;
      var accs = bundleAccessories(bd);
      var nowUnit = unitPrice(v);
      var wasUnit = regUnit(v);
      accs.forEach(function (a) { nowUnit += (a.price || 0); wasUnit += regUnit(a); });
      var nowCents = nowUnit * qty;
      var wasCents = wasUnit * qty;
      var saveCents = wasCents - nowCents;
      var savePct = wasCents > 0 ? Math.round((saveCents / wasCents) * 100) : 0;
      var hasSave = saveCents > 0 && savePct > 0;
      var single = accs.length === 0 && qty === 1;
      var nowM = (single && v && v.priceMoney) ? v.priceMoney : money(nowCents);
      var wasM = (single && v && v.compareMoney) ? v.compareMoney : money(wasCents);
      var saveM = (single && v && v.saveMoney) ? v.saveMoney : money(saveCents);
      return {
        nowCents: nowCents, nowM: nowM, qty: qty,
        wasCents: wasCents, wasM: wasM,
        saveCents: saveCents, saveM: saveM,
        savePct: savePct, hasSave: hasSave
      };
    }

    /* Emil: blur-mask the price as it changes — visual only, never gates the
       instant state feedback (aria-pressed etc. update synchronously). */
    var swapT;
    function flashPricing() {
      if (RM || !pricing) return;
      pricing.classList.add("is-swapping");
      clearTimeout(swapT);
      swapT = setTimeout(function () { pricing.classList.remove("is-swapping"); }, 170);
    }

    function paint() {
      var v = resolveVariant();

      sizeBtns.forEach(function (b) {
        var s = b.getAttribute("data-k-size");
        b.setAttribute("aria-pressed", s === state.size ? "true" : "false");
        if (!variants[s]) b.setAttribute("disabled", "");
      });

      /* re-query so an editor section reload can't leave us with stale nodes */
      colorBtns = $$("[data-k-color]", form);
      colorBtns.forEach(function (b) {
        var c = b.getAttribute("data-k-color");
        /* every brand colour is always visible and always selectable — a colour
           with no live variant is handled by resolveVariant()'s fallback + the
           stock line, never by hiding or disabling the swatch */
        b.hidden = false;
        b.removeAttribute("disabled");
        b.setAttribute("aria-pressed", c === state.color ? "true" : "false");
      });
      if (colorName) colorName.textContent = state.color || "";

      var b = bundles[state.bundle] || {};
      bundleBtns.forEach(function (btn, i) {
        btn.setAttribute("aria-pressed", i === state.bundle ? "true" : "false");
        var bd = bundles[i] || {};
        var t = bundleTotals(bd, v);
        var pEl = btn.querySelector("[data-k-bundle-price]");
        if (pEl) pEl.textContent = t.nowM;
        var wEl = btn.querySelector("[data-k-bundle-was]");
        if (wEl) { wEl.textContent = t.hasSave ? t.wasM : ""; wEl.hidden = !t.hasSave; }
        var sEl = btn.querySelector("[data-k-bundle-save]");
        if (sEl) {
          sEl.textContent = t.hasSave ? ("You save " + t.saveM + " · " + t.savePct + "% off") : "";
          sEl.hidden = !t.hasSave;
        }
        btn.setAttribute("data-saves", t.hasSave ? "true" : "false");
        /* keep the "Power Gun — Colour" line in the contents list current */
        var gunName = btn.querySelector("[data-k-bundle-gunname]");
        if (gunName) gunName.textContent = (cfg.productLabel || "Power Gun") + (state.color ? " — " + state.color : "");
      });

      if (stockEl) {
        var inStock = !!(v && v.available);
        stockEl.setAttribute("data-out", inStock ? "false" : "true");
        var lbl = stockEl.querySelector("[data-k-stock-label]");
        if (lbl) lbl.textContent = inStock ? (stockEl.getAttribute("data-label-in") || "In stock") : (stockEl.getAttribute("data-label-out") || "Currently unavailable");
      }

      var tt = bundleTotals(b, v);
      var kitShort = shortKitName(b);
      var stickyLabel = kitShort + (b && (b.includes || []).length === 0 && state.color ? " · " + state.color : "");

      if (priceNow) priceNow.textContent = tt.nowM;
      if (priceWas) {
        priceWas.textContent = tt.hasSave ? tt.wasM : "";
        priceWas.hidden = !tt.hasSave;
      }
      if (priceSave) {
        priceSave.textContent = tt.hasSave ? ("You save " + tt.saveM + " · " + tt.savePct + "% off") : "";
        priceSave.hidden = !tt.hasSave;
      }
      if (idInput) idInput.value = v ? v.id : "";
      if (atc) {
        var canBuy = !!(v && v.available) && accessoriesAvailable(b);
        atc.textContent = canBuy ? ("Add " + kitShort + " · " + tt.nowM) : (v && !v.available ? "Sold out" : "Currently unavailable");
        atc.setAttribute("aria-disabled", canBuy ? "false" : "true");
        atc.setAttribute("data-kit", kitShort);
      }
      if (stickyName) stickyName.textContent = stickyLabel;
      if (stickyPrice) stickyPrice.textContent = tt.nowM;
      var sAtc = $("[data-k-sticky-atc]");
      if (sAtc) sAtc.textContent = "Add " + kitShort;
      $$("[data-k-split-sync]").forEach(function (sec) { setSplit(sec, state.size, false); });
    }
    function shortKitName(b) {
      if (!b || (b.includes || []).length === 0) return "Power Gun";
      var n = (b.name || "Kit").replace(/grooming\s+kit/i, "Kit").replace(/\s+/g, " ").trim();
      return n;
    }
    function accessoriesAvailable(b) {
      return bundleAccessories(b).every(function (a) { return a.available !== false; });
    }
    /* only sync the gallery to the variant image after a real colour choice —
       never on load (so a lifestyle hero image isn't yanked away). */
    var syncGallery = false;
    function render() { paint(); }
    function renderSwap() { flashPricing(); paint(); }

    sizeBtns.forEach(function (btn) { on(btn, "click", function () { state.size = btn.getAttribute("data-k-size"); renderSwap(); }); });

    /* delegate colour + bundle clicks on the form so they keep working even if
       the Theme Editor swaps the inner DOM (stale per-button listeners were why
       a re-selected colour sometimes did nothing) */
    /* colour swatches — direct listener on each button. Re-queried every boot,
       so an editor DOM swap just rebinds. Selecting any colour ALWAYS lands,
       including returning to the default one. */
    function pickColor(btn) {
      state.color = btn.getAttribute("data-k-color");
      $$("[data-k-color]", form).forEach(function (b2) { b2.setAttribute("aria-pressed", b2 === btn ? "true" : "false"); });
      if (colorName) colorName.textContent = state.color;
      syncGallery = true;
      try { swapColorImage(btn); } catch (_) {}
      renderSwap();
    }
    $$("[data-k-color]", form).forEach(function (b) { on(b, "click", function () { pickColor(b); }); });

    /* bundles — delegated on the form (no direct binding elsewhere) */
    on(form, "click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var bd = t.closest("[data-k-bundle]");
      if (bd && form.contains(bd)) {
        state.bundle = $$("[data-k-bundle]", form).indexOf(bd);
        renderSwap();
      }
    });

    function setFormError(msg) {
      var box = $("[data-k-buy-error]", form);
      if (!box) { box = document.createElement("p"); box.className = "k-buy__error"; box.setAttribute("data-k-buy-error", ""); if (atc && atc.parentNode) atc.parentNode.insertBefore(box, atc.nextSibling); }
      box.textContent = msg || "";
      box.hidden = !msg;
    }
    var addBusy = false;
    async function addToCart() {
      if (addBusy || !atc || atc.getAttribute("data-loading") === "true" || atc.getAttribute("aria-disabled") === "true") return;
      var v = resolveVariant();
      var b = bundles[state.bundle] || {};
      if (!v || !v.id) { setFormError("Choose an available colour first."); return; }
      if (!accessoriesAvailable(b)) { setFormError("A part of this kit is out of stock right now."); return; }
      addBusy = true;
      setFormError("");
      var restore = atc.textContent;
      atc.setAttribute("data-loading", "true");
      atc.setAttribute("aria-busy", "true");
      atc.textContent = "Adding…";
      var selectedCard = bundleBtns[state.bundle];
      var bundleId = newBundleId();
      try {
        Upsell.shown = false;
        var items = bundleItems(b, v, bundleId, 1);
        var isKit = items.length > 1;   /* has accessories → a grouped bundle */
        if (isKit) {
          /* same tier + same Power Gun colour already in the cart? bump that
             group instead of adding a second identical card. */
          var existing = null;
          try { existing = findBundleBySig(await Cart.get(), bundleSig(items)); } catch (e) {}
          if (existing) {
            var mainLine = existing.lines.filter(function (l) { return (l.properties || {})._bundle_role === "main"; })[0] || existing.lines[0];
            var target = (mainLine.quantity || 1) + 1;
            var up = {};
            existing.lines.forEach(function (l) { up[l.key] = target; });
            await Cart.update(up);
            bundleId = existing.id;
          } else {
            await Cart.add(items);
          }
        } else {
          /* standalone Power Gun — Shopify merges identical variant+properties for us */
          await Cart.add(items);
        }
        /* optional post-add upsell (kept, unrelated to the kit) */
        var accepted = false;
        try { accepted = await Upsell.offer(); } catch (e) {}
        if (accepted && window.KLYRO.upsellVariantId) {
          try { await Cart.add([{ id: window.KLYRO.upsellVariantId, quantity: 1 }]); } catch (e) {}
        }
        var cart = null;
        try { cart = await Cart.get(); updateCount(cart); } catch (e) {}
        CartDrawer.lastBundleId = bundleId;
        var opened = false;
        try { CartDrawer.open(); opened = !!CartDrawer.el; } catch (e) {}
        if (!opened) toast("Added to cart");
        /* Emil: a brief, quiet confirm on the button + the chosen card */
        if (!RM) {
          atc.setAttribute("data-added", "true");
          if (selectedCard) { selectedCard.setAttribute("data-just-added", "true"); setTimeout(function () { selectedCard.removeAttribute("data-just-added"); }, 900); }
          setTimeout(function () { atc.removeAttribute("data-added"); paint(); }, 1100);
        } else { paint(); }
        window.dispatchEvent(new CustomEvent("klyro:add", { detail: { variantId: v.id, bundleId: bundleId } }));
      } catch (e) {
        setFormError((e && e.message) ? ("Couldn't add to cart — " + e.message) : "Couldn't add to cart — please try again.");
      } finally {
        atc.removeAttribute("data-loading");
        atc.removeAttribute("aria-busy");
        addBusy = false;
        if (atc.getAttribute("data-added") !== "true") atc.textContent = restore;
      }
    }
    on(atc, "click", addToCart);

    /* review line → smooth-scroll to the real reviews section further down */
    var reviewsLink = $("[data-k-scroll-reviews]", form);
    if (reviewsLink) {
      on(reviewsLink, "click", function (e) {
        var target = document.querySelector("#reviews, .k-testi-sec, [data-k-reviews], #shopify-section-testimonials");
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: RM ? "auto" : "smooth", block: "start" });
        }
      });
    }

    var stickyAtc = $("[data-k-sticky-atc]");
    on(stickyAtc, "click", addToCart);
    var sticky = $("[data-k-stickybuy]");
    if (sticky && atc && "IntersectionObserver" in window) {
      new IntersectionObserver(function (ents) {
        ents.forEach(function (e) { sticky.setAttribute("data-show", e.isIntersecting ? "false" : "true"); });
      }, { rootMargin: "-140px 0px 0px 0px" }).observe(atc);
    }

    /* size-jump buttons anywhere on the page */
    $$("[data-k-choose-size]").forEach(function (btn) {
      on(btn, "click", function (e) {
        e.preventDefault();
        state.size = btn.getAttribute("data-k-choose-size");
        renderSwap();
        var t = $("#klyro-buy");
        if (t) t.scrollIntoView({ behavior: RM ? "auto" : "smooth", block: "center" });
      });
    });

    /* expose for the split section's local toggle */
    window.KLYRO._setSize = function (s) { state.size = s; renderSwap(); };

    render();
  }

  /* ================================================ EDITORIAL SPLIT (Mini/Max) */
  function setSplit(sec, sizeVal, propagate) {
    var toggle = $(".k-split__toggle", sec);
    var idx = sizeVal && sizeVal.toLowerCase() === "max" ? 1 : 0;
    if (toggle) toggle.setAttribute("data-i", String(idx));
    $$(".k-split__toggle button", sec).forEach(function (b, i) { b.setAttribute("aria-pressed", i === idx ? "true" : "false"); });
    $$(".k-split__img", sec).forEach(function (im) { im.setAttribute("data-active", im.getAttribute("data-size") && im.getAttribute("data-size").toLowerCase() === (idx ? "max" : "mini") ? "true" : "false"); });
    $$(".k-split__panel", sec).forEach(function (p) { p.setAttribute("data-active", (p.getAttribute("data-size") || "").toLowerCase() === (idx ? "max" : "mini") ? "true" : "false"); });
    if (propagate && window.KLYRO && window.KLYRO._setSize) window.KLYRO._setSize(idx ? "Max" : "Mini");
  }
  function initSplit() {
    $$("[data-k-split]").forEach(function (sec) {
      if (!once(sec, "split")) return;
      $$(".k-split__toggle button", sec).forEach(function (b) {
        on(b, "click", function () { setSplit(sec, b.getAttribute("data-size"), true); });
      });
      setSplit(sec, "mini", false);
    });
  }

  /* ========================================================= COUNTDOWN */
  function initCountdown() {
    $$("[data-k-countdown]").forEach(function (el) {
      if (!once(el, "countdown")) return;
      var deadlineAttr = el.getAttribute("data-deadline");
      var durMin = parseInt(el.getAttribute("data-duration"), 10);
      var target;
      if (deadlineAttr) { target = new Date(deadlineAttr).getTime(); if (isNaN(target)) return; }
      else if (durMin > 0) {
        var key = "klyro_cd_" + (el.getAttribute("data-key") || "offer");
        var saved = 0;
        try { saved = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) {}
        if (!saved || saved < Date.now()) { saved = Date.now() + durMin * 60000; try { localStorage.setItem(key, String(saved)); } catch (e) {} }
        target = saved;
      } else return;

      var d = el.querySelector("[data-cd-d]"), h = el.querySelector("[data-cd-h]"), m = el.querySelector("[data-cd-m]"), s = el.querySelector("[data-cd-s]");
      var dw = el.querySelector("[data-cd-dwrap]");
      var pad = function (n) { return n < 10 ? "0" + n : "" + n; };
      function tick() {
        var diff = target - Date.now();
        if (diff <= 0 && durMin > 0) { target = Date.now() + durMin * 60000; try { localStorage.setItem("klyro_cd_" + (el.getAttribute("data-key") || "offer"), String(target)); } catch (e) {} diff = target - Date.now(); }
        if (diff < 0) diff = 0;
        var t = Math.floor(diff / 1000);
        var dd = Math.floor(t / 86400); t -= dd * 86400;
        var hh = Math.floor(t / 3600); t -= hh * 3600;
        var mm = Math.floor(t / 60); t -= mm * 60;
        if (d) d.textContent = pad(dd); if (dw) dw.hidden = dd <= 0;
        if (h) h.textContent = pad(hh); if (m) m.textContent = pad(mm); if (s) s.textContent = pad(t);
      }
      tick(); setInterval(tick, 1000);
    });
  }

  /* ============================== VIDEO MARQUEE — auto-scrolls, plays in view.
     Centre-focus: each card swells as its centre crosses the viewport middle and
     eases back down on the way out (spatial feedback — transform/opacity only). */
  function initVideoMarquee() {
    $$("[data-k-vmarq]").forEach(function (root) {
      if (!once(root, "vmarq")) return;
      var track = $(".k-vmarq__track", root);
      if (!track) return;
      var originals = Array.prototype.slice.call(track.children);
      if (!originals.length) return;

      if (!RM) {
        /* duplicate for a seamless -50% loop — clones are decorative */
        originals.forEach(function (node) {
          var c = node.cloneNode(true);
          c.setAttribute("aria-hidden", "true");
          track.appendChild(c);
        });
        var per = parseFloat(root.getAttribute("data-speed")) || 7; /* seconds per card */
        track.style.setProperty("--k-vmarq-dur", (originals.length * per) + "s");
      }

      var cards = $$(".k-vmarq__card", track);
      var vids = $$("video", track);
      function play() { vids.forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }); }
      function stop() { vids.forEach(function (v) { v.pause(); }); }

      /* ---- centre-focus scale loop (only while the strip is on screen) ---- */
      var raf = 0, visible = false;
      function frame() {
        raf = 0;
        var vw = window.innerWidth;
        var mid = vw / 2;
        var reach = Math.min(vw * 0.6, 620);        /* distance over which it falls off */
        for (var i = 0; i < cards.length; i++) {
          var c = cards[i];
          var r = c.getBoundingClientRect();
          if (r.right < -120 || r.left > vw + 120) { c.style.removeProperty("--k-vm-s"); continue; }
          var d = Math.abs(r.left + r.width / 2 - mid) / reach;
          if (d > 1) d = 1;
          var t = 1 - d;
          t = t * t * (3 - 2 * t);                  /* smoothstep — gentle at the peak */
          c.style.setProperty("--k-vm-s", (0.9 + 0.155 * t).toFixed(3));
          c.style.setProperty("--k-vm-o", (0.58 + 0.42 * t).toFixed(3));
          c.style.zIndex = (t * 10) | 0;
        }
        if (visible) raf = requestAnimationFrame(frame);
      }
      function startFocus() { if (!raf && visible && !RM) raf = requestAnimationFrame(frame); }

      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (ents) {
          ents.forEach(function (e) {
            visible = e.isIntersecting;
            root.setAttribute("data-paused", visible ? "false" : "true");
            if (visible) { play(); startFocus(); } else { stop(); }
          });
        }, { threshold: 0.15 }).observe(root);
      } else { visible = true; play(); startFocus(); }
    });
  }

  /* ============================================ BEFORE / AFTER SLIDER
     The transparent full-size range input handles click-to-position, drag and
     keyboard; we mirror its value onto --k-ba-x AND write clip-path / left
     inline on the elements, so the split still works even if the stylesheet
     rule that reads --k-ba-x is overridden or missing. */
  function initBeforeAfter() {
    $$("[data-k-ba]").forEach(function (root) {
      if (!once(root, "ba")) return;
      var range = $(".k-ba__range", root);
      if (!range) return;
      var over = $(".k-ba__over", root);
      var divider = $(".k-ba__divider", root);
      function apply() {
        var pct = range.value + "%";
        root.style.setProperty("--k-ba-x", pct);
        if (over) over.style.clipPath = "inset(0 calc(100% - " + pct + ") 0 0)";
        if (divider) divider.style.left = pct;
      }
      apply();
      on(range, "input", apply);
      /* also let a press+drag anywhere on the image move the split */
      var dragging = false;
      function toX(clientX) {
        var r = root.getBoundingClientRect();
        if (!r.width) return;
        var pct = clamp(Math.round(((clientX - r.left) / r.width) * 100), 0, 100);
        range.value = pct; apply();
      }
      on(root, "pointerdown", function (e) { dragging = true; try { root.setPointerCapture(e.pointerId); } catch (_) {} toX(e.clientX); });
      on(root, "pointermove", function (e) { if (dragging) toX(e.clientX); });
      on(root, "pointerup", function () { dragging = false; });
      on(root, "pointercancel", function () { dragging = false; });
    });
  }

  /* ====================================================== REVIEW MARQUEE */
  function initMarquee() {
    $$("[data-k-marquee]").forEach(function (track) {
      if (RM || !once(track, "marquee")) return;
      var n = track.children.length;
      if (!n) return;
      track.innerHTML += track.innerHTML;
      var per = parseFloat(track.getAttribute("data-speed")) || 6;
      track.style.setProperty("--k-marquee-dur", (n * per) + "s");
    });
  }

  /* ============================================== SPRAY DIAL (signature) */
  function initDial() {
    $$("[data-k-dial]").forEach(function (root) {
      if (!once(root, "dial")) return;
      var modes = $$(".k-dial__mode", root);
      var frames = $$(".k-dial__frame", root);
      var bar = $(".k-dial__progress span", root);
      if (!modes.length) return;
      var i = 0, timer = null, hovering = false;
      var DWELL = parseInt(root.getAttribute("data-dwell"), 10) || 3600;

      function show(n, user) {
        i = (n + modes.length) % modes.length;
        modes.forEach(function (m, mi) { m.setAttribute("aria-pressed", mi === i ? "true" : "false"); });
        frames.forEach(function (f, fi) {
          f.setAttribute("data-active", fi === i ? "true" : "false");
          f.setAttribute("aria-hidden", fi === i ? "false" : "true");
        });
        if (user) restart();
      }
      function tickBar(start) {
        if (RM || !bar) return;
        var t0 = performance.now();
        cancelAnimationFrame(bar._raf);
        (function step(now) {
          if (hovering) { bar._raf = requestAnimationFrame(step); return; }
          var p = clamp((now - t0) / DWELL, 0, 1);
          bar.style.transform = "scaleX(" + p + ")";
          if (p < 1) bar._raf = requestAnimationFrame(step);
        })(t0);
      }
      function restart() {
        clearInterval(timer);
        if (RM) return;
        tickBar();
        timer = setInterval(function () { if (!hovering) { show(i + 1); tickBar(); } }, DWELL);
      }
      modes.forEach(function (m, mi) {
        on(m, "click", function () { show(mi, true); });
        on(m, "mouseenter", function () { hovering = true; });
        on(m, "mouseleave", function () { hovering = false; });
      });
      on(root, "mouseleave", function () { hovering = false; });
      show(0);
      if (!RM && "IntersectionObserver" in window) {
        new IntersectionObserver(function (ents) {
          ents.forEach(function (e) { e.isIntersecting ? restart() : clearInterval(timer); });
        }, { threshold: 0.35 }).observe(root);
      } else { restart(); }
    });
  }

  /* =============================================================== BOOT
     Every module runs in its own try/catch so ONE broken module can never
     take down the rest. The purchase flow (initBuy, CartDrawer) runs first
     so it's never a casualty of anything else. */
  function boot() {
    $$(".klyro").forEach(function (el) { el.classList.add("k-js"); });
    var mods = [
      ["buy", initBuy],
      ["cart", function () { CartDrawer.init(); }],
      ["upsell", function () { Upsell.init(); }],
      ["header", initHeader],
      ["reveal", initReveal],
      ["heroGallery", initHeroGallery],
      ["ctaLight", initCtaLight],
      ["faq", initFaq],
      ["story", initStory],
      ["split", initSplit],
      ["dial", initDial],
      ["videoMarquee", initVideoMarquee],
      ["beforeAfter", initBeforeAfter],
      ["countdown", initCountdown],
      ["marquee", initMarquee]
    ];
    mods.forEach(function (m) {
      try { m[1](); }
      catch (e) { if (window.console && console.warn) console.warn("[klyro] " + m[0] + " init failed:", e); }
    });

    var hero = document.querySelector("#klyro-hero");
    if (hero) {
      var host = hero.closest(".klyro") || hero;
      requestAnimationFrame(function () { host.setAttribute("data-hero-in", "true"); });
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
